import { Box, Button, Skeleton, Stack } from "@mantine/core"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useNavigate, useParams } from "react-router"
import useSwr from "swr"
import { OrderApi } from "../../apis"
import { PageLayout } from "../../components/layout"
import { EDITABLE_STATUSES } from "../../components/order/order-actions"
import { ListState } from "../../components/ui"
import { SupplierSalesForm } from "../../components/supplier-sales/form"
import { IAuth } from "../../store/Auth"
import { IGeneral } from "../../store/General"

/**
 * Захиалга засах. The backend only updates the sender's DRAFT, REGISTERED and REVIEW_REJECTED orders
 * (order/update.ts); any other order opened by URL gets a «Засах боломжгүй» card instead of a form that
 * fails with `error.order.not_found` on save.
 */
export function SupplierSalesEdit() {
  const params = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation(["supplierSales", "common"])
  const user = useSelector((s: { auth: IAuth }) => s.auth.user)
  const orderStatus = useSelector((s: { general: IGeneral }) => s.general.orderStatus)

  const { data, error, isLoading, mutate } = useSwr(
    `/ord/order/supplier/get.${params.id}.${params.receiverBusinessId}`,
    async () => await OrderApi.get(params.id as string),
  )

  const frame = (children: React.ReactNode) => (
    <PageLayout
      title={t("supplierSales:sales.editOrder")}
      breadcrumb={[
        { to: "/", label: t("common:breadcrumb.home") },
        { to: "/supplier-sales", label: t("supplierSales:sales.title") },
        { label: t("supplierSales:sales.editOrder") },
      ]}>
      <Box>{children}</Box>
    </PageLayout>
  )
  const back = (
    <Button variant="default" onClick={() => navigate("/supplier-sales")}>{t("supplierSales:detail.back")}</Button>
  )

  if (isLoading && !data) {
    return frame(
      <Stack gap="md">
        <Skeleton height={220} radius="lg" />
        <Skeleton height={260} radius="lg" />
      </Stack>,
    )
  }

  const status = (error as { statusCode?: number } | undefined)?.statusCode
  if (status === 404 || (!error && !data)) {
    return frame(
      <ListState
        state="empty"
        title={t("supplierSales:detail.notFoundTitle")}
        description={t("supplierSales:detail.notFoundText")}
        action={{ label: t("supplierSales:detail.back"), onClick: () => navigate("/supplier-sales") }}
      />,
    )
  }
  if (error) {
    return frame(
      <Stack gap="md" align="flex-start">
        <ListState state="error" onRetry={() => mutate()} errorCode={status} />
        {back}
      </Stack>,
    )
  }

  const isOwner = !!user?.currentBusiness?.id && user.currentBusiness.id === data?.senderBusiness?.id
  const editable = (EDITABLE_STATUSES as readonly string[]).includes(data?.orderStatus)
  if (!isOwner || !editable) {
    const statusName = orderStatus.find((c: { code: string }) => c.code === data?.orderStatus)?.sentName ?? data?.orderStatus ?? "-"
    return frame(
      <ListState
        state="empty"
        title={t("supplierSales:edit.blockedTitle")}
        description={isOwner ? t("supplierSales:edit.blockedStatus", { status: statusName }) : t("supplierSales:edit.blockedOwner")}
        action={{ label: t("supplierSales:detail.back"), onClick: () => navigate("/supplier-sales") }}
      />,
    )
  }

  return <SupplierSalesForm action={["edit", data]} />
}
